/**
 * Settings: resident imports, a pointer to Manage access, and Integrations.
 *
 * ─── WHY IT IS NOT IN THE MAIN NAV ───────────────────────────────────────────
 * It sits at the BOTTOM of the sidebar, beside the signed-in email and Sign out,
 * because that is what it is: account-level plumbing, not one of the four things
 * a tenant opens this portal to do (approve parkers, check permits, issue
 * validations, read billing). The demo has no Settings tab at all.
 *
 * ─── WHY USERS ARE NOT MANAGED HERE ──────────────────────────────────────────
 * This screen used to mount a second Users manager. It drifted from the
 * canonical one (UsersPanel, opened by "Manage access") in two ways a tenant
 * could see (TEN-03 in docs/TENANT-PARITY-MAP.md):
 *   - its "Send invite" saved the login and never asked for the sign-in email;
 *   - it picked the ACTOR as the first roster row that was either the signed-in
 *     user or the designated contact, so a lower-level contact whose email
 *     sorted first hid Invite and Revoke from the Full-access user looking at it.
 * One access manager means one invite path, one capability check
 * (`tenantCan(me, 'users.manage')`), and one place for the last-Full and self
 * guards. This card only opens it. Do not grow a roster back in here.
 *
 * ─── WHAT LEFT, AND WHY ──────────────────────────────────────────────────────
 * Tablets (Stations) used to live here behind a sub-nav, justified by a comment
 * claiming the demo "never depicted" it. That was wrong — the demo renders
 * Stations inside its Validation area (`TValidation` takes `stations`/
 * `setStations`), and it belongs there for a better reason than parity: a paired
 * tablet is the thing that ISSUES validations, so it reads with the code a guest
 * scans and the log of what was redeemed, not with an API key.
 *
 * The sub-nav went with it. One screen behind two pills is a control that only
 * ever has one answer. If a second settings screen ever lands, bring the pills
 * back with it rather than nesting this one deeper now.
 */
import type { CSSProperties } from 'react'
import type { TenantSession } from '../lib/session'
import { Integrations } from './Integrations'
import { ResidentImports } from './ResidentImports'
import { cardBody, ghostBtn, screenTitle } from '../ui'

interface Props {
  session: TenantSession
  /** Opens the canonical Manage access panel (App owns its open state). */
  onManageAccess: () => void
}

export function Settings({ session, onManageAccess }: Props) {
  return <>
    <ResidentImports session={session} />
    <AccessPointer onManageAccess={onManageAccess} />
    <Integrations session={session} />
  </>
}

function AccessPointer({ onManageAccess }: { onManageAccess: () => void }) {
  return <section aria-labelledby="settings-access-title" style={{ marginBottom: 28 }}>
    <h2 id="settings-access-title" style={{ ...screenTitle, marginBottom: 6 }}>Users and access</h2>
    <div style={card}>
      <p style={{ ...cardBody, margin: 0, flex: '1 1 260px' }}>
        Invite teammates, change what each person can do, and choose the designated contact in Manage access.
      </p>
      <button type="button" onClick={onManageAccess} style={ghostBtn}>Manage access</button>
    </div>
  </section>
}

const card: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
  background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: 16,
}
